import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PaginatedAsyncSelect from '../../../components/form/PaginatedAsyncSelect';
import { formatDimensions } from '../../../shared/units';
import { PRODUCT_DIMENSION_SIDES } from '../productDimensions';
import type { ProductDimensionsFormData, ProductMeasurements, UnitOption } from '../types';

const mapUnitOption = (entry: { id: number; symbol: string }): UnitOption => ({
  value: entry.id,
  label: entry.symbol,
});

interface ProductDimensionsFieldsProps {
  value: ProductDimensionsFormData;
  onChange: (patch: Partial<ProductDimensionsFormData>) => void;
}

/** Length × width × height inputs with their unit; switching the unit keeps the entered numbers. */
export const ProductDimensionsFields = ({ value, onChange }: ProductDimensionsFieldsProps) => {
  const { t } = useTranslation();

  return (
    <Form.Group className="mb-3">
      <Form.Label>{t('warehouses.products.dimensions')}</Form.Label>
      <Row className="g-2">
        {PRODUCT_DIMENSION_SIDES.map((side) => (
          <Col xs={6} md={3} key={side}>
            <Form.Control
              id={`product-${side}`}
              type="number"
              min="0"
              step="any"
              value={value[side]}
              onChange={(event) => onChange({ [side]: event.target.value })}
              placeholder={t(`warehouses.products.${side}`)}
              aria-label={t(`warehouses.products.${side}`)}
            />
          </Col>
        ))}
        <Col xs={6} md={3}>
          <PaginatedAsyncSelect
            inputId="product-dimension_unit"
            aria-label={t('warehouses.products.dimensionUnit')}
            value={value.dimension_unit_option}
            onChange={(option: UnitOption | null) => onChange({ dimension_unit_option: option })}
            endpoint="settings/api/dimension-units/"
            mapOption={mapUnitOption}
            isClearable={false}
            placeholder={t('warehouses.products.dimensionUnit')}
            noOptionsMessage={() => t('warehouses.products.unitNotFound')}
          />
        </Col>
      </Row>
    </Form.Group>
  );
};

/** Read-only datagrid item: "50 × 40 × 30 cm" or "—". */
export const ProductDimensionsInfo = ({ product }: { product: ProductMeasurements }) => {
  const { t } = useTranslation();

  return (
    <div className="datagrid-item">
      <div className="datagrid-title">{t('warehouses.products.dimensions')}</div>
      <div className="datagrid-content">
        {formatDimensions(product.length, product.width, product.height, product.dimension_unit_symbol)}
      </div>
    </div>
  );
};
